import React from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ModalButtonType, type IModalDialogContent, type ModalDialogOptions, type ModalWindow } from './Modal';
import { CodeHighlighter } from './CodeHighlighter';
import { ImgCopy } from './Images/ImgCopy';
import { ImgDocumentCheck } from './Images/ImgDocumentCheck';

import './tool-definition.css';

export type ToolDefinitionDialogProps = {
  title: string;
  definition: unknown;
  /** Optional subtitle shown under the title, e.g. the MCP server name. */
  subtitle?: string;
};

/**
 * Read-only modal that pretty-prints and syntax-highlights a tool's full JSON
 * definition, with a copy-to-clipboard button. Shared by the MCP Settings tab
 * and the Chat Tools tab.
 */
@observer
export default class ToolDefinitionDialog extends React.Component<ToolDefinitionDialogProps> implements IModalDialogContent<void> {
  @observable private _copied = false;
  // Props copied into local observables (mobx-react forbids reading props in reactive contexts).
  @observable private readonly _title: string;
  @observable private readonly _subtitle: string | undefined;
  @observable private readonly _definition: unknown;
  private _copyTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ToolDefinitionDialogProps) {
    super(props);
    this._title = props.title;
    this._subtitle = props.subtitle;
    this._definition = props.definition;
    makeObservable(this);
  }

  componentWillUnmount() {
    if (this._copyTimer) clearTimeout(this._copyTimer);
  }

  private get _json(): string {
    try {
      return JSON.stringify(this._definition, null, 2) ?? '';
    } catch {
      return String(this._definition);
    }
  }

  public getModalOptions(_window: ModalWindow<void>): ModalDialogOptions<void> {
    return {
      title: (
        <div className="tdd-title">
          <span className="tdd-title-name">{this._title}</span>
          {this._subtitle && <span className="tdd-title-sub">{this._subtitle}</span>}
        </div>
      ),
      width: 'min(760px, 92vw)',
      className: 'tdd-modal',
      buttons: [
        {
          type: ModalButtonType.Close,
          title: 'Close',
          onClick: (w) => w.close(ModalButtonType.Close),
        },
      ],
    };
  }

  @action.bound
  private async _handleCopy() {
    try {
      await navigator.clipboard.writeText(this._json);
      this._copied = true;
      if (this._copyTimer) clearTimeout(this._copyTimer);
      this._copyTimer = setTimeout(() => { this._copied = false; }, 2000);
    } catch {
      // Clipboard unavailable (e.g. non-secure context) — fall back to selection-based copy.
      const pre = document.querySelector('.tdd-json code, .tdd-json pre');
      if (pre) {
        const range = document.createRange();
        range.selectNodeContents(pre);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        document.execCommand('copy');
        sel?.removeAllRanges();
      }
    }
  }

  render() {
    return (
      <div className="tdd">
        <div className="tdd-toolbar">
          <button
            className={`tdd-copy-btn${this._copied ? ' tdd-copy-btn--copied' : ''}`}
            onClick={this._handleCopy}
          >
            {this._copied ? <ImgDocumentCheck size={13} /> : <ImgCopy size={13} />}
            <span>{this._copied ? 'Copied!' : 'Copy JSON'}</span>
          </button>
        </div>
        <div className="tdd-json">
          <CodeHighlighter code={this._json} language="json" />
        </div>
      </div>
    );
  }
}
